// src/utils/dateUtils.js
import { Timestamp } from 'firebase/firestore';

/**
 * Safely convert any Firebase timestamp, date string, or numeric timestamp
 * to a JavaScript Date object.
 * @param {any} timestamp - Firestore Timestamp, Date, string, or number
 * @returns {Date|null}
 */
export const convertToDate = (timestamp) => {
  if (!timestamp) return null;

  try {
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }

    if (timestamp instanceof Date) {
      return isNaN(timestamp.getTime()) ? null : new Date(timestamp.getTime());
    }

    if (typeof timestamp === 'string') {
      if (timestamp.includes(' at ')) {
        const date = new Date(timestamp.split(' at ')[0]);
        if (!isNaN(date.getTime())) return date;
      }

      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) return date;

      const numeric = parseInt(timestamp, 10);
      if (!isNaN(numeric)) {
        return numeric > 10000000000 ? new Date(numeric) : new Date(numeric * 1000);
      }

      return null;
    }

    if (typeof timestamp === 'number') {
      return timestamp > 10000000000 ? new Date(timestamp) : new Date(timestamp * 1000);
    }

    if (typeof timestamp === 'object' && timestamp !== null && typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000);
    }

    return null;
  } catch (error) {
    console.error("Error converting timestamp to Date:", error);
    return null;
  }
};

/**
 * Validate a date and return a default if invalid.
 * @param {Date} date
 * @param {Date} defaultDate
 * @returns {Date}
 */
export const validateDate = (date, defaultDate) => {
  return (date instanceof Date && !isNaN(date.getTime())) ? date : defaultDate;
};

/**
 * Format a date for display.
 * @param {Date|Timestamp|string|number|null} dateValue
 * @param {string} format - 'short', 'long', 'time', 'datetime', 'input' (default: 'short')
 * @param {string} defaultText - Returned when date is invalid (default: 'N/A')
 * @returns {string}
 */
export const formatDate = (dateValue, format = 'short', defaultText = 'N/A') => {
  const date = convertToDate(dateValue);
  if (!date) return defaultText;

  try {
    switch (format) {
      case 'long':
        return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
      case 'time':
        return date.toLocaleTimeString();
      case 'datetime':
        return date.toLocaleString();
      case 'input':
        return formatDateForInput(date);
      case 'short':
      default:
        return date.toLocaleDateString();
    }
  } catch (e) {
    console.error("Date formatting error:", e);
    return defaultText;
  }
};

/**
 * Format a date as YYYY-MM-DD for HTML date inputs.
 * @param {Date|Timestamp|string|number|null} dateValue
 * @returns {string}
 */
export const formatDateForInput = (dateValue) => {
  const date = convertToDate(dateValue);
  if (!date) return '';

  try {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch (e) {
    console.error("Error formatting date for input:", e);
    return '';
  }
};

/**
 * Convert any date format to a Firestore Timestamp.
 * @param {Date|Timestamp|string|number|null} dateValue
 * @returns {Timestamp|null}
 */
export const toFirestoreTimestamp = (dateValue) => {
  if (!dateValue) return null;
  if (dateValue instanceof Timestamp) return dateValue;

  const date = convertToDate(dateValue);
  return date ? Timestamp.fromDate(date) : null;
};

/**
 * Check whether a date falls between two others (inclusive).
 * @param {Date|Timestamp|string|number|null} dateValue
 * @param {Date|Timestamp|string|number|null} startDateValue
 * @param {Date|Timestamp|string|number|null} endDateValue
 * @returns {boolean}
 */
export const isDateBetween = (dateValue, startDateValue, endDateValue) => {
  const date = convertToDate(dateValue);
  const startDate = convertToDate(startDateValue);
  const endDate = convertToDate(endDateValue);

  if (!date || !startDate) return false;

  const dateTime = date.getTime();
  const endTime = endDate ? endDate.getTime() : Infinity;

  return dateTime >= startDate.getTime() && dateTime <= endTime;
};

/**
 * Compare two dates for sorting. Returns -1, 0, or 1.
 * @param {Date|Timestamp|string|number|null} dateValueA
 * @param {Date|Timestamp|string|number|null} dateValueB
 * @returns {number}
 */
export const compareDates = (dateValueA, dateValueB) => {
  const dateA = convertToDate(dateValueA);
  const dateB = convertToDate(dateValueB);

  if (!dateA && !dateB) return 0;
  if (!dateA) return -1;
  if (!dateB) return 1;

  const timeA = dateA.getTime();
  const timeB = dateB.getTime();
  return timeA < timeB ? -1 : timeA > timeB ? 1 : 0;
};

const dateUtils = {
  convertToDate,
  validateDate,
  formatDate,
  formatDateForInput,
  toFirestoreTimestamp,
  isDateBetween,
  compareDates
};

export default dateUtils;